import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLocation } from 'react-router-dom';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DebouncedTextarea } from '../components/ui/debounced-textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Send, 
  Sparkles, 
  FileText, 
  Plus,
  Edit,
  Trash2,
  Loader2,
  Copy,
  Paperclip,
  X,
  Eye,
  CheckCircle,
  Building2,
  Users,
  Clock,
  Calendar,
  Search,
  Filter,
  Mail,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Check,
  Save,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDateTime } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { AttachmentList } from '../components/AttachmentPreview';
import { HelpButton } from '../components/help';
import { CampaignResultsTab } from '../components/CampaignResultsTab';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Debounce hook for performance
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const categories = [
  { value: 'initial_contact', label: 'Initial Contact' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'confirmation', label: 'Confirmation' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'other', label: 'Other' },
];

const tones = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];

export const EmailHubPage = () => {
  const { isBarbiePink } = useTheme();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('compose');
  const [templates, setTemplates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const fileInputRef = useRef(null);
  const templateFileInputRef = useRef(null);
  
  // Track if we've applied prefill data
  const [prefillApplied, setPrefillApplied] = useState(false);
  
  // Compose form
  const [composeData, setComposeData] = useState({
    to_email: '',
    to_name: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    org_id: '',
    contact_id: '',
    attachments: [],
    ai_polished: false,
    // Nudge settings - default disabled
    // nudge_1_hours: hours (0=disabled)
    // nudge_2_days: days (0=disabled), stored as hours internally
    // nudge_3_weeks: weeks (0=disabled), stored as hours internally
    nudge_enabled: false,
    nudge_1_hours: 48,
    nudge_2_days: 7,   // 7 days = 168 hours
    nudge_3_weeks: 2   // 2 weeks = 336 hours
  });
  const [ccBccOpen, setCcBccOpen] = useState(false);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Template dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    category: '',
    attachments: []
  });
  
  // AI Polish
  const [polishTone, setPolishTone] = useState('professional');
  const [originalBody, setOriginalBody] = useState('');
  const [showPolishConfirm, setShowPolishConfirm] = useState(false);
  const [polishedText, setPolishedText] = useState('');

  // Bulk Email State
  const [sectors, setSectors] = useState([]);
  const [bulkOrganizations, setBulkOrganizations] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSectorFilter, setBulkSectorFilter] = useState('all');
  const [selectedSectors, setSelectedSectors] = useState([]); // Multi-select sectors
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');
  const debouncedBulkSearchTerm = useDebounce(bulkSearchTerm, 300); // Debounce search to prevent rapid API calls
  // Year + Marketing Status filters for the Bulk Email picker. Persisted
  // via state only (not in URL) — operators usually flip these a few times
  // per session, and storing in URL would clutter the share-link.
  const [bulkYearFilter, setBulkYearFilter] = useState('all');
  const [bulkMarketingStatusFilter, setBulkMarketingStatusFilter] = useState('all');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [bulkSortBy, setBulkSortBy] = useState('name'); // 'name', 'sector', 'email'
  const [bulkSortOrder, setBulkSortOrder] = useState('asc'); // 'asc', 'desc'
  const [alphabetFilter, setAlphabetFilter] = useState('all'); // 'all', 'A-E', 'F-L', 'M-R', 'S-Z'
  const [bulkCampaignName, setBulkCampaignName] = useState('');
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkSelectedTemplate, setBulkSelectedTemplate] = useState('');
  const [bulkScheduledDate, setBulkScheduledDate] = useState('');
  const [bulkScheduledTime, setBulkScheduledTime] = useState('');
  const [bulkCreating, setBulkCreating] = useState(false);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [viewingBulkEmail, setViewingBulkEmail] = useState(null);
  const [editingBulkEmail, setEditingBulkEmail] = useState(null);

  // Campaign Templates State
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [campaignTemplatesLoading, setCampaignTemplatesLoading] = useState(false);
  const [selectedCampaignTemplate, setSelectedCampaignTemplate] = useState('');
  const [savingCampaignTemplate, setSavingCampaignTemplate] = useState(false);
  const [campaignTemplateName, setCampaignTemplateName] = useState('');
  const [showSaveCampaignDialog, setShowSaveCampaignDialog] = useState(false);
  const [editingCampaignTemplate, setEditingCampaignTemplate] = useState(null);

  // Bulk Email AI Assist State
  const [bulkPolishing, setBulkPolishing] = useState(false);
  const [bulkPolishTone, setBulkPolishTone] = useState('professional');
  const [bulkPolishedText, setBulkPolishedText] = useState('');
  const [showBulkPolishConfirm, setShowBulkPolishConfirm] = useState(false);
  const [bulkOriginalBody, setBulkOriginalBody] = useState('');
  const [bulkAttachments, setBulkAttachments] = useState([]);
  const bulkFileInputRef = useRef(null);

  // Email Audit State (moved from EmailAuditPage)
  const [auditEmails, setAuditEmails] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearchTerm, setAuditSearchTerm] = useState('');
  const [auditOrgFilter, setAuditOrgFilter] = useState('all');
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [viewingAuditEmail, setViewingAuditEmail] = useState(null);
  const [updatingNudge, setUpdatingNudge] = useState(false);

  // Toggle nudge for a sent email
  const toggleEmailNudge = async (email, enabled) => {
    setUpdatingNudge(true);
    try {
      const response = await authJsonFetch(`${API}/sent-emails/${email.email_id}/nudge`, {
        method: 'PUT',
        body: JSON.stringify({ enabled })
      });
      if (response.ok) {
        const updated = await response.json();
        // Update the viewing email
        setViewingAuditEmail(prev => prev ? { ...prev, nudge_enabled: enabled } : prev);
        // Update in the list
        setAuditEmails(prev => prev.map(e => 
          e.email_id === email.email_id ? { ...e, nudge_enabled: enabled } : e
        ));
        toast.success(enabled ? 'Nudge reminders enabled' : 'Nudge reminders disabled');
      } else {
        toast.error('Failed to update nudge settings');
      }
    } catch (err) {
      console.error('Failed to toggle nudge:', err);
      toast.error('Failed to update nudge settings');
    } finally {
      setUpdatingNudge(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchOrganizations();
    fetchSectors();
  }, []);

  // Helper function to replace template variables
  const replaceTemplateVariables = (text, orgName, contactName) => {
    if (!text) return text;
    return text
      .replace(/\{\{Org_name\}\}/gi, orgName || '')
      .replace(/\{\{Pri_contact\}\}/gi, contactName || '');
  };

  // Handle prefilled data from navigation (e.g., from Organizations page)
  useEffect(() => {
    if (location.state?.prefillEmail && !prefillApplied) {
      const prefill = location.state.prefillEmail;
      const template = prefill.template;
      
      // Get contact name (primary contact) and org name
      // Use primary_contact_name for {{Pri_contact}} variable, to_name is the recipient name
      const contactName = prefill.primary_contact_name || prefill.to_name || 'there';
      const orgName = prefill.org_name || '';
      
      let emailSubject = '';
      let emailBody = '';
      
      if (template) {
        // Use template with variable substitution
        emailSubject = replaceTemplateVariables(template.subject || '', orgName, contactName);
        emailBody = replaceTemplateVariables(template.body || '', orgName, contactName);
        
        // If subject doesn't include org name, prepend it
        if (orgName && !emailSubject.toLowerCase().includes(orgName.toLowerCase())) {
          emailSubject = `${orgName} - ${emailSubject}`;
        }
        
        toast.success(`Using template: ${template.name}`);
      } else {
        // Create personalized email body for compose new - use primary contact name
        emailBody = `Dear ${contactName},

I hope this email finds you well.

${orgName ? `I am reaching out regarding ${orgName}.` : ''}

[Your message here]

Best regards,
G.S Workflow Team`;
        
        emailSubject = orgName ? `${orgName} - ` : '';
        
        if (prefill.org_name) {
          toast.info(`Composing email for ${prefill.org_name}`);
        }
      }
      
      setComposeData(prev => ({
        ...prev,
        to_email: prefill.to_email || '',
        to_name: contactName, // Use primary contact name as recipient name
        org_id: prefill.org_id || '',
        body: emailBody,
        subject: emailSubject,
        // Carry the template's attachments through to compose so they actually
        // get sent. Without this, navigating from an org with a template that
        // has attachments silently dropped them.
        attachments: (template && Array.isArray(template.attachments))
          ? template.attachments
          : (prev.attachments || [])
      }));
      setPrefillApplied(true);
      setActiveTab('compose');
      if (template && Array.isArray(template.attachments) && template.attachments.length > 0) {
        toast.info(`${template.attachments.length} attachment(s) loaded from template`);
      }
      
      // Fetch contacts if org_id is provided
      if (prefill.org_id) {
        fetchContacts(prefill.org_id);
      }
    }
  }, [location.state, prefillApplied]);

  // Bulk-prefill flow: Data Preview can navigate here with a list of org_ids
  // for the filtered rows. We fetch their primary contacts in one go and
  // pre-select them on the Bulk tab, sparing the operator from re-finding
  // each org manually. Runs ONCE per navigation; location state is then
  // wiped so a route bounce doesn't re-fire.
  // Bulk prefill from Data Preview was removed Feb 26 2026 — the flow is
  // now handled in-place via the BulkEmailCampaignDialog rendered on the
  // Data Preview page. The receiver below remains only as a guard so
  // any stale links carrying `state.prefillBulk` redirect cleanly back
  // to /export-data instead of silently reactivating the buggy code
  // path. Do NOT re-add the per-recipient fetch here; build it inside
  // BulkEmailCampaignDialog instead.
  useEffect(() => {
    if (location.state?.prefillBulk) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
    }
  }, [location.state]);

  const fetchTemplates = async () => {
    try {
      const response = await authFetch(`${API}/email-templates`);
      if (response.ok) setTemplates(await response.json());
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await authFetch(`${API}/organizations?limit=1000`);
      if (response.ok) {
        const result = await response.json();
        // Handle both paginated response and array response
        setOrganizations(result.data || result);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const fetchContacts = async (orgId) => {
    try {
      const response = await authFetch(`${API}/contacts?org_id=${orgId}&limit=500`);
      if (response.ok) {
        const result = await response.json();
        setContacts(result.data || result);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  };

  const handleOrgChange = (orgId) => {
    const actualOrgId = orgId === 'none' ? '' : orgId;
    setComposeData({...composeData, org_id: actualOrgId, contact_id: ''});
    if (actualOrgId) {
      fetchContacts(actualOrgId);
    } else {
      setContacts([]);
    }
  };

  const handleContactChange = (contactId) => {
    const actualContactId = contactId === 'none' ? '' : contactId;
    const contact = contacts.find(c => c.contact_id === actualContactId);
    setComposeData({
      ...composeData, 
      contact_id: actualContactId,
      to_email: contact?.email || '',
      to_name: contact?.name || ''
    });
  };

  const handleFileUpload = async (e, forTemplate = false) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Upload each file
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const token = localStorage.getItem('gs_session_token');
        const response = await fetch(`${API}/attachments`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });

        if (response.ok) {
          const attachment = await response.json();
          if (forTemplate) {
            setTemplateForm(prev => ({
              ...prev,
              attachments: [...prev.attachments, attachment]
            }));
          } else {
            setComposeData(prev => ({
              ...prev,
              attachments: [...prev.attachments, attachment]
            }));
          }
          toast.success(`${file.name} attached`);
        } else {
          const error = await response.json().catch(() => ({}));
          toast.error(error.detail || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        toast.error(`Upload error: ${file.name}`);
      }
    }

    // Reset file input
    if (forTemplate && templateFileInputRef.current) {
      templateFileInputRef.current.value = '';
    } else if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (attachmentId, forTemplate = false) => {
    if (forTemplate) {
      setTemplateForm({
        ...templateForm,
        attachments: templateForm.attachments.filter(a => a.attachment_id !== attachmentId)
      });
    } else {
      setComposeData({
        ...composeData,
        attachments: composeData.attachments.filter(a => a.attachment_id !== attachmentId)
      });
    }
  };

  // Handle bulk campaign file uploads
  const handleBulkFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const token = localStorage.getItem('gs_session_token');
        const response = await fetch(`${API}/attachments`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });

        if (response.ok) {
          const attachment = await response.json();
          setBulkAttachments(prev => [...prev, attachment]);
          toast.success(`${file.name} attached`);
        } else {
          const error = await response.json().catch(() => ({}));
          toast.error(error.detail || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        toast.error(`Upload error: ${file.name}`);
      }
    }

    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  const removeBulkAttachment = (attachmentId) => {
    setBulkAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
  };

  const handlePreview = async () => {
    if (!composeData.to_email || !composeData.subject || !composeData.body) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await authJsonFetch(`${API}/email/preview`, {
        method: 'POST',
        body: JSON.stringify(composeData)
      });

      if (response.ok) {
        const preview = await response.json();
        setPreviewData(preview);
        setPreviewOpen(true);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (err) {
      toast.error('Preview error');
    }
  };

  const handleSendEmail = async () => {
    setLoading(true);
    try {
      // Convert nudge_2_days to hours and nudge_3_weeks to hours for backend
      const sendData = {
        ...composeData,
        nudge_2_hours: (composeData.nudge_2_days || 0) * 24,
        nudge_3_hours: (composeData.nudge_3_weeks || 0) * 24 * 7
      };
      
      const response = await authJsonFetch(`${API}/send-email`, {
        method: 'POST',
        body: JSON.stringify(sendData)
      });

      if (response.ok) {
        toast.success('Email sent successfully');
        setPreviewOpen(false);
        setComposeData({
          to_email: '',
          to_name: '',
          cc: '',
          bcc: '',
          subject: '',
          body: '',
          org_id: '',
          contact_id: '',
          attachments: [],
          ai_polished: false,
          nudge_enabled: false,
          nudge_1_hours: 48,
          nudge_2_days: 7,
          nudge_3_weeks: 2
        });
        setCcBccOpen(false);
      } else {
        toast.error('Failed to send email');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePolishEmail = async () => {
    if (!composeData.body) {
      toast.error('Please enter email content to polish');
      return;
    }
    
    setPolishing(true);
    setOriginalBody(composeData.body);
    
    try {
      const response = await authJsonFetch(`${API}/ai/polish-email`, {
        method: 'POST',
        body: JSON.stringify({
          text: composeData.body,
          tone: polishTone
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPolishedText(data.polished_text);
        setShowPolishConfirm(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to polish email');
      }
    } catch (err) {
      toast.error('AI service error');
    } finally {
      setPolishing(false);
    }
  };

  const acceptPolishedText = () => {
    setComposeData({...composeData, body: polishedText, ai_polished: true});
    setShowPolishConfirm(false);
    toast.success('Polished text applied');
  };

  const rejectPolishedText = () => {
    setShowPolishConfirm(false);
    toast.info('Kept original text');
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      const url = editingTemplate 
        ? `${API}/email-templates/${editingTemplate.template_id}`
        : `${API}/email-templates`;
      
      const response = await authJsonFetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        body: JSON.stringify(templateForm)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setTemplateDialogOpen(false);
        resetTemplateForm();
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const response = await authFetch(`${API}/email-templates/${templateId}`, { method: 'DELETE' });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const applyTemplate = (template) => {
    // Get current org and contact names for variable replacement
    const currentOrg = organizations.find(o => o.org_id === composeData.org_id);
    const orgName = currentOrg?.name || '';
    const contactName = composeData.to_name || '';
    
    // Apply template with variable substitution
    const processedSubject = replaceTemplateVariables(template.subject, orgName, contactName);
    const processedBody = replaceTemplateVariables(template.body, orgName, contactName);
    
    setComposeData({
      ...composeData,
      subject: processedSubject,
      body: processedBody,
      attachments: template.attachments || []
    });
    setActiveTab('compose');
    toast.success('Template loaded - variables replaced');
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      body: '',
      category: '',
      attachments: []
    });
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category,
      attachments: template.attachments || []
    });
    setTemplateDialogOpen(true);
  };

  // ==================== BULK EMAIL FUNCTIONS ====================

  const fetchSectors = async () => {
    try {
      const response = await authFetch(`${API}/sectors`);
      if (response.ok) {
        const data = await response.json();
        console.log('Sectors loaded:', data.length, data);
        setSectors(data);
      } else {
        console.error('Failed to fetch sectors - response not ok:', response.status);
      }
    } catch (err) {
      console.error('Failed to fetch sectors:', err);
    }
  };

  const fetchBulkOrganizations = useCallback(async (searchValue, alphaFilter) => {
    setBulkLoading(true);
    try {
      let url = `${API}/organizations-for-bulk-email?`;
      // Support both single select (bulkSectorFilter) and multi-select (selectedSectors)
      if (selectedSectors.length > 0) {
        // Multi-select: filter client-side after fetching all
        // For now, fetch all and filter client-side
      } else if (bulkSectorFilter && bulkSectorFilter !== 'all') {
        url += `sector_id=${bulkSectorFilter}&`;
      }
      
      // Year + marketing_status filters (Feb 2026 user request) — when set,
      // restrict to orgs whose sales_data row for that year carries the
      // matching status. Backend handles either filter independently.
      const hasYear = bulkYearFilter && bulkYearFilter !== 'all';
      const hasStatus = bulkMarketingStatusFilter && bulkMarketingStatusFilter !== 'all';
      if (hasYear) url += `year=${encodeURIComponent(bulkYearFilter)}&`;
      if (hasStatus) url += `marketing_status=${encodeURIComponent(bulkMarketingStatusFilter)}&`;

      // Alphabetical range — pushed to backend so the filter scans the whole
      // org collection, not just the first 20-500 rows the server happens to
      // return. Operators reported F-L / M-R / S-Z coming up empty because
      // the entire result set was alphabetised and capped at the start.
      const hasAlpha = alphaFilter && alphaFilter !== 'all';
      if (hasAlpha) url += `name_range=${encodeURIComponent(alphaFilter)}&`;

      // Remove limit when searching, filtering by alphabet, sectors, year, or marketing status
      const hasActiveFilter = searchValue || hasAlpha || selectedSectors.length > 0 || hasYear || hasStatus;
      if (hasActiveFilter) {
        url += `limit=0`; // No limit when any filter is active - fetch all
        if (searchValue) {
          url += `&search=${encodeURIComponent(searchValue)}`;
        }
      } else {
        url += `limit=20`; // Show only 20 by default when no filters active
      }
      
      console.log('Fetching bulk organizations from:', url);
      const response = await authFetch(url);
      if (response.ok) {
        let data = await response.json();
        console.log('Bulk organizations loaded:', data.length, data.slice(0, 3));
        
        // If multi-sector selected, filter client-side
        if (selectedSectors.length > 0) {
          data = data.filter(org => selectedSectors.includes(org.sector_id));
        }
        
        setBulkOrganizations(data);
      } else {
        console.error('Failed to fetch organizations - response not ok:', response.status);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    } finally {
      setBulkLoading(false);
    }
  }, [bulkSectorFilter, selectedSectors, bulkYearFilter, bulkMarketingStatusFilter]);

  const fetchScheduledEmails = async () => {
    setScheduledLoading(true);
    try {
      const response = await authFetch(`${API}/bulk-email?status=pending`);
      if (response.ok) {
        const data = await response.json();
        setScheduledEmails(data.emails || []);
        setScheduledCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Failed to fetch scheduled emails:', err);
    } finally {
      setScheduledLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'bulk') {
      fetchBulkOrganizations(debouncedBulkSearchTerm, alphabetFilter);
      fetchScheduledEmails();
    } else if (activeTab === 'audit') {
      fetchAuditEmails();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'bulk') {
      fetchBulkOrganizations(debouncedBulkSearchTerm, alphabetFilter);
    }
  }, [bulkSectorFilter, selectedSectors, debouncedBulkSearchTerm, alphabetFilter, bulkYearFilter, bulkMarketingStatusFilter, fetchBulkOrganizations, activeTab]);

  const handleBulkTemplateSelect = (templateId) => {
    setBulkSelectedTemplate(templateId);
    if (templateId) {
      const template = templates.find(t => t.template_id === templateId);
      if (template) {
        setBulkSubject(template.subject || '');
        setBulkBody(template.body || '');
      }
    }
  };

  // Campaign Template Functions
  const fetchCampaignTemplates = async () => {
    setCampaignTemplatesLoading(true);
    try {
      const response = await authFetch(`${API}/campaign-templates`);
      if (response.ok) {
        const data = await response.json();
        setCampaignTemplates(data);
      }
    } catch (err) {
      console.error('Failed to fetch campaign templates:', err);
    } finally {
      setCampaignTemplatesLoading(false);
    }
  };

  const loadCampaignTemplate = (templateId) => {
    const template = campaignTemplates.find(t => t.template_id === templateId);
    if (template) {
      setBulkSubject(template.subject_template || '');
      setBulkBody(template.body_template || '');
      setBulkAttachments(template.attachments || []);
      setSelectedCampaignTemplate(templateId);
      toast.success(`Loaded campaign template: ${template.name}`);
    }
  };

  const saveCampaignTemplate = async () => {
    if (!campaignTemplateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!bulkSubject.trim() && !bulkBody.trim()) {
      toast.error('Please enter a subject or body to save');
      return;
    }

    setSavingCampaignTemplate(true);
    try {
      const response = await authJsonFetch(`${API}/campaign-templates`, {
        method: 'POST',
        body: JSON.stringify({
          name: campaignTemplateName,
          subject_template: bulkSubject,
          body_template: bulkBody,
          attachments: bulkAttachments
        })
      });

      if (response.ok) {
        const newTemplate = await response.json();
        setCampaignTemplates(prev => [newTemplate, ...prev]);
        setShowSaveCampaignDialog(false);
        setCampaignTemplateName('');
        setSelectedCampaignTemplate(newTemplate.template_id);
        toast.success('Campaign template saved');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSavingCampaignTemplate(false);
    }
  };

  const updateCampaignTemplate = async (templateId) => {
    if (!bulkSubject.trim() && !bulkBody.trim()) {
      toast.error('Please enter a subject or body to save');
      return;
    }

    setSavingCampaignTemplate(true);
    try {
      const response = await authJsonFetch(`${API}/campaign-templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify({
          subject_template: bulkSubject,
          body_template: bulkBody,
          attachments: bulkAttachments
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setCampaignTemplates(prev => prev.map(t => t.template_id === templateId ? updated : t));
        toast.success('Campaign template updated');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update template');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setSavingCampaignTemplate(false);
    }
  };

  const deleteCampaignTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this campaign template?')) {
      return;
    }

    try {
      const response = await authJsonFetch(`${API}/campaign-templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setCampaignTemplates(prev => prev.filter(t => t.template_id !== templateId));
        if (selectedCampaignTemplate === templateId) {
          setSelectedCampaignTemplate('');
        }
        toast.success('Campaign template deleted');
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  // Load campaign templates on mount
  useEffect(() => {
    fetchCampaignTemplates();
  }, []);

  const toggleRecipient = (org) => {
    setSelectedRecipients(prev => {
      const exists = prev.find(r => r.org_id === org.org_id);
      if (exists) {
        return prev.filter(r => r.org_id !== org.org_id);
      } else {
        return [...prev, {
          org_id: org.org_id,
          org_name: org.org_name,
          contact_email: org.contact_email,
          contact_name: org.contact_name,
          sector_id: org.sector_id
        }];
      }
    });
  };

  // Alphabetical filter ranges
  const alphabetRanges = [
    { value: 'all', label: 'All', range: null },
    { value: 'A-E', label: 'A-E', range: ['A', 'E'] },
    { value: 'F-L', label: 'F-L', range: ['F', 'L'] },
    { value: 'M-R', label: 'M-R', range: ['M', 'R'] },
    { value: 'S-Z', label: 'S-Z', range: ['S', 'Z'] },
  ];

  // Sorted bulk organizations. Alphabetical filtering is now done server-side
  // (see fetchBulkOrganizations → name_range param) so the picker can drill
  // into the full org collection instead of only the first 20-500 rows.
  const sortedBulkOrganizations = useMemo(() => {
    let filtered = [...bulkOrganizations];
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (bulkSortBy) {
        case 'name':
          comparison = (a.org_name || '').localeCompare(b.org_name || '');
          break;
        case 'sector':
          comparison = (a.sector_id || '').localeCompare(b.sector_id || '');
          break;
        case 'email':
          comparison = (a.contact_email || '').localeCompare(b.contact_email || '');
          break;
        case 'contact':
          comparison = (a.contact_name || '').localeCompare(b.contact_name || '');
          break;
        default:
          comparison = 0;
      }
      return bulkSortOrder === 'desc' ? -comparison : comparison;
    });
    return filtered;
  }, [bulkOrganizations, bulkSortBy, bulkSortOrder]);

  // Toggle sector selection for multi-select
  const toggleSectorSelection = (sectorId) => {
    setSelectedSectors(prev => {
      if (prev.includes(sectorId)) {
        return prev.filter(id => id !== sectorId);
      } else {
        return [...prev, sectorId];
      }
    });
    // Clear single-select when using multi-select
    setBulkSectorFilter('all');
  };

  // Select all sectors
  const selectAllSectors = () => {
    setSelectedSectors(sectors.map(s => s.sector_id));
    setBulkSectorFilter('all');
  };

  // Clear sector selection
  const clearSectorSelection = () => {
    setSelectedSectors([]);
    setBulkSectorFilter('all');
  };

  // Get sector display name by ID
  const getSectorName = (sectorId, fallbackName) => {
    // Prefer the resolved name baked into the org row by the backend —
    // covers the prefill flow where the /api/sectors fetch may not yet
    // have landed by the time the recipient table renders.
    if (fallbackName) return fallbackName;
    const sector = sectors.find(s => s.sector_id === sectorId);
    return sector?.display_name || sectorId;
  };

  const selectAllRecipients = () => {
    setSelectedRecipients(sortedBulkOrganizations.map(org => ({
      org_id: org.org_id,
      org_name: org.org_name,
      contact_email: org.contact_email,
      contact_name: org.contact_name,
      sector_id: org.sector_id
    })));
  };

  const clearAllRecipients = () => {
    setSelectedRecipients([]);
  };

  // Helper to split array into chunks
  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const createBulkEmailCampaign = async () => {
    if (!bulkCampaignName.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    if (!bulkSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!bulkBody.trim()) {
      toast.error('Please enter an email body');
      return;
    }
    if (!bulkScheduledDate || !bulkScheduledTime) {
      toast.error('Please set a scheduled date and time');
      return;
    }

    // Combine date and time
    const scheduledAt = new Date(`${bulkScheduledDate}T${bulkScheduledTime}`);
    const minTime = new Date(Date.now() + 2 * 60 * 60 * 1000); // 2 hours from now

    if (scheduledAt < minTime) {
      toast.error('Scheduled time must be at least 2 hours from now');
      return;
    }

    setBulkCreating(true);
    try {
      const BATCH_SIZE = 50;
      const recipientBatches = chunkArray(selectedRecipients, BATCH_SIZE);
      const totalBatches = recipientBatches.length;
      let successCount = 0;
      let errorMessages = [];

      // Create a campaign for each batch
      for (let i = 0; i < recipientBatches.length; i++) {
        const batch = recipientBatches[i];
        const batchName = totalBatches > 1 
          ? `${bulkCampaignName} (Batch ${i + 1}/${totalBatches})`
          : bulkCampaignName;

        const response = await authJsonFetch(`${API}/bulk-email`, {
          method: 'POST',
          body: JSON.stringify({
            name: batchName,
            recipients: batch,
            template_id: bulkSelectedTemplate || null,
            subject_template: bulkSubject,
            body_template: bulkBody,
            scheduled_at: scheduledAt.toISOString(),
            attachments: bulkAttachments
          })
        });

        if (response.ok) {
          successCount++;
        } else {
          const error = await response.json();
          errorMessages.push(`Batch ${i + 1}: ${error.detail || 'Failed'}`);
        }
      }

      // Show results
      if (successCount === totalBatches) {
        if (totalBatches > 1) {
          toast.success(`Created ${totalBatches} email campaigns with ${selectedRecipients.length} total recipients`);
        } else {
          toast.success(`Created email campaign with ${selectedRecipients.length} recipients`);
        }
        // Reset form
        setBulkCampaignName('');
        setSelectedRecipients([]);
        setBulkSubject('');
        setBulkBody('');
        setBulkSelectedTemplate('');
        setBulkScheduledDate('');
        setBulkScheduledTime('');
        setBulkAttachments([]);
      } else if (successCount > 0) {
        toast.warning(`Created ${successCount}/${totalBatches} campaigns. Errors: ${errorMessages.join(', ')}`);
      } else {
        toast.error(`Failed to create campaigns: ${errorMessages.join(', ')}`);
      }
      
      // Refresh scheduled emails
      fetchScheduledEmails();
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setBulkCreating(false);
    }
  };

  const deleteBulkEmail = async (bulkEmailId) => {
    try {
      const response = await authFetch(`${API}/bulk-email/${bulkEmailId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Scheduled email deleted');
        fetchScheduledEmails();
      } else {
        toast.error('Failed to delete');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const deleteBulkCampaign = async (campaignId) => {
    try {
      const response = await authFetch(`${API}/bulk-email/campaign/${campaignId}`, { method: 'DELETE' });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchScheduledEmails();
      } else {
        toast.error('Failed to delete campaign');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  // ==================== BULK EMAIL AI ASSIST FUNCTIONS ====================

  const handleBulkPolishEmail = async () => {
    if (!bulkBody) {
      toast.error('Please enter email content to polish');
      return;
    }
    
    setBulkPolishing(true);
    setBulkOriginalBody(bulkBody);
    
    try {
      const response = await authJsonFetch(`${API}/ai/polish-email`, {
        method: 'POST',
        body: JSON.stringify({
          text: bulkBody,
          tone: bulkPolishTone
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBulkPolishedText(data.polished_text);
        setShowBulkPolishConfirm(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to polish email');
      }
    } catch (err) {
      toast.error('AI service error');
    } finally {
      setBulkPolishing(false);
    }
  };

  const acceptBulkPolishedText = () => {
    setBulkBody(bulkPolishedText);
    setShowBulkPolishConfirm(false);
    toast.success('AI polished text applied');
  };

  const rejectBulkPolishedText = () => {
    setShowBulkPolishConfirm(false);
    toast.info('Kept original text');
  };

  // ==================== EMAIL AUDIT FUNCTIONS ====================

  const fetchAuditEmails = async (page = 1) => {
    setAuditLoading(true);
    try {
      let url = `${API}/sent-emails?page=${page}&limit=20`;
      if (auditOrgFilter && auditOrgFilter !== 'all') url += `&org_id=${auditOrgFilter}`;
      if (auditSearchTerm) url += `&search=${encodeURIComponent(auditSearchTerm)}`;
      
      const response = await authFetch(url);
      if (response.ok) {
        const result = await response.json();
        setAuditEmails(result.data || []);
        setAuditPage(result.page || 1);
        setAuditTotalPages(result.total_pages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch audit emails:', err);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditEmails(1);
    }
  }, [auditOrgFilter, auditSearchTerm]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Australia/Sydney' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney' });
  };

  return (
    <div data-testid="email-hub-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Email Hub</h1>
        <p className="text-muted-foreground mt-1">
          Compose emails with AI assistance and manage templates
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="compose" data-testid="compose-tab">
            <Send className="w-4 h-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="bulk" data-testid="bulk-email-tab">
            <Users className="w-4 h-4 mr-2" />
            Bulk Email
          </TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="campaigns-tab">
            <BarChart3 className="w-4 h-4 mr-2" />
            Campaign Results
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="audit-tab">
            <Mail className="w-4 h-4 mr-2" />
            Email Audit
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Email Form */}
            <Card className={cn("lg:col-span-2", isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle>Compose Email</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Organization (Optional)</Label>
                      <Select value={composeData.org_id || 'none'} onValueChange={handleOrgChange}>
                        <SelectTrigger data-testid="email-org-select">
                          <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {organizations.map(org => (
                            <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Contact (Optional)</Label>
                      <Select 
                        value={composeData.contact_id || 'none'} 
                        onValueChange={handleContactChange}
                        disabled={!composeData.org_id}
                      >
                        <SelectTrigger data-testid="email-contact-select">
                          <SelectValue placeholder="Select contact" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {contacts.map(contact => (
                            <SelectItem key={contact.contact_id} value={contact.contact_id}>
                              {contact.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="to_email" className="flex items-center justify-between">
                        <span>To Email *</span>
                        <button
                          type="button"
                          className="text-[11px] text-primary hover:underline"
                          onClick={() => setCcBccOpen(v => !v)}
                          data-testid="email-toggle-cc-bcc"
                        >
                          {ccBccOpen ? 'Hide Cc/Bcc' : 'Add Cc/Bcc'}
                        </button>
                      </Label>
                      <Input
                        id="to_email"
                        type="email"
                        data-testid="email-to-input"
                        value={composeData.to_email}
                        onChange={(e) => setComposeData({...composeData, to_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="to_name">Recipient Name</Label>
                      <Input
                        id="to_name"
                        data-testid="email-name-input"
                        value={composeData.to_name}
                        onChange={(e) => setComposeData({...composeData, to_name: e.target.value})}
                      />
                    </div>
                  </div>

                  {ccBccOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="email-cc-bcc-row">
                      <div>
                        <Label htmlFor="cc">Cc</Label>
                        <Input
                          id="cc"
                          type="text"
                          data-testid="email-cc-input"
                          placeholder="e.g. alice@example.com, bob@example.com"
                          value={composeData.cc}
                          onChange={(e) => setComposeData({...composeData, cc: e.target.value})}
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">Separate multiple addresses with commas.</p>
                      </div>
                      <div>
                        <Label htmlFor="bcc">Bcc</Label>
                        <Input
                          id="bcc"
                          type="text"
                          data-testid="email-bcc-input"
                          placeholder="e.g. archive@example.com"
                          value={composeData.bcc}
                          onChange={(e) => setComposeData({...composeData, bcc: e.target.value})}
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">Your address is always Bcc'd automatically.</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      data-testid="email-subject-input"
                      value={composeData.subject}
                      onChange={(e) => setComposeData({...composeData, subject: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="body">Email Body *</Label>
                    <DebouncedTextarea
                      id="body"
                      data-testid="email-body-input"
                      value={composeData.body}
                      onChange={(e) => setComposeData(prev => ({...prev, body: e.target.value}))}
                      rows={10}
                      className="resize-none"
                      debounceMs={300}
                    />
                    {composeData.ai_polished && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        AI Polished
                      </p>
                    )}
                  </div>

                  {/* Nudge Settings */}
                  <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium">Follow-up Nudges</Label>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="nudge-toggle" className="text-sm">
                          {composeData.nudge_enabled ? 'Enabled' : 'Disabled'}
                        </Label>
                        <input
                          id="nudge-toggle"
                          type="checkbox"
                          checked={composeData.nudge_enabled}
                          onChange={(e) => setComposeData({...composeData, nudge_enabled: e.target.checked})}
                          className="w-4 h-4"
                          data-testid="nudge-enabled-checkbox"
                        />
                      </div>
                    </div>
                    
                    {composeData.nudge_enabled && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">1st Nudge (hours)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={composeData.nudge_1_hours}
                            onChange={(e) => setComposeData({...composeData, nudge_1_hours: parseInt(e.target.value) || 0})}
                            className="h-8"
                            data-testid="nudge-1-input"
                            placeholder="0 = disabled"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">2nd Nudge (days)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={composeData.nudge_2_days}
                            onChange={(e) => setComposeData({...composeData, nudge_2_days: parseInt(e.target.value) || 0})}
                            className="h-8"
                            data-testid="nudge-2-input"
                            placeholder="0 = disabled"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">3rd Nudge (weeks)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={composeData.nudge_3_weeks}
                            onChange={(e) => setComposeData({...composeData, nudge_3_weeks: parseInt(e.target.value) || 0})}
                            className="h-8"
                            data-testid="nudge-3-input"
                            placeholder="0 = disabled"
                          />
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      {composeData.nudge_enabled 
                        ? `Reminders: ${composeData.nudge_1_hours > 0 ? `${composeData.nudge_1_hours}h` : 'off'}, ${composeData.nudge_2_days > 0 ? `${composeData.nudge_2_days} days` : 'off'}, ${composeData.nudge_3_weeks > 0 ? `${composeData.nudge_3_weeks} weeks` : 'off'}`
                        : 'No automatic follow-up reminders'
                      }
                    </p>
                  </div>

                  {/* Attachments */}
                  <div>
                    <Label className="flex items-center gap-2">
                      Attachments
                      {composeData.attachments.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {composeData.attachments.length}
                        </Badge>
                      )}
                    </Label>
                    <div className="mt-2 space-y-2">
                      <AttachmentList
                        attachments={composeData.attachments}
                        onRemove={(id) => removeAttachment(id)}
                        showPreview={true}
                        emptyMessage="No attachments added"
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => handleFileUpload(e, false)}
                        className="hidden"
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Paperclip className="w-4 h-4 mr-2" />
                        Add Attachments
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button 
                      variant="outline"
                      onClick={handlePreview}
                      data-testid="preview-email-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview & Send
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Polish Panel */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  AI Polish
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Use Gemini AI to refine your email for professional tone and clarity.
                </p>
                
                <div>
                  <Label>Tone</Label>
                  <Select value={polishTone} onValueChange={setPolishTone}>
                    <SelectTrigger data-testid="polish-tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tones.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  data-testid="polish-email-btn"
                  onClick={handlePolishEmail} 
                  disabled={polishing || !composeData.body}
                  className="w-full"
                >
                  {polishing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Polish with AI
                </Button>

                <p className="text-xs text-muted-foreground">
                  You'll be able to review and approve changes before applying.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button 
              data-testid="add-template-btn"
              onClick={() => { resetTemplateForm(); setTemplateDialogOpen(true); }}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Template
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.length === 0 ? (
              <Card className={cn("col-span-full", isBarbiePink && "card-brutalist")}>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No templates yet. Create your first template to get started.
                </CardContent>
              </Card>
            ) : (
              templates.map((template) => (
                <Card key={template.template_id} className={cn(isBarbiePink && "card-brutalist")}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {template.name}
                      {template.attachments?.length > 0 && (
                        <Badge variant="secondary">
                          <Paperclip className="w-3 h-3 mr-1" />
                          {template.attachments.length}
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {template.category.replace('_', ' ')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-medium mb-2">{template.subject}</p>
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {template.body}
                    </p>
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="outline" onClick={() => applyTemplate(template)}>
                        <Copy className="w-3 h-3 mr-1" />
                        Use
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditTemplate(template)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-destructive"
                        onClick={() => handleDeleteTemplate(template.template_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Bulk Email Tab */}
        <TabsContent value="bulk" className="mt-6">
          <div className="space-y-6">
            {/* Header with Help Button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Bulk Email Campaign</h2>
                <p className="text-sm text-muted-foreground">Send emails to multiple organizations at once</p>
              </div>
              <HelpButton topic="bulk_email" />
            </div>
            
            {/* Select Recipients Card */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Select Recipients
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Filter organizations by sector or name, then select contacts for bulk email
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sector Selection - Multi-select chips */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Filter by Sector
                    </Label>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={selectAllSectors}
                        className="h-7 text-xs"
                        data-testid="select-all-sectors-btn"
                      >
                        Select All
                      </Button>
                      {selectedSectors.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={clearSectorSelection}
                          className="h-7 text-xs text-destructive"
                        >
                          Clear ({selectedSectors.length})
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {sectors.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Loading sectors...</p>
                    ) : (
                      sectors.map(sector => (
                        <Badge
                          key={sector.sector_id}
                          variant={selectedSectors.includes(sector.sector_id) ? "default" : "outline"}
                          className={cn(
                            "cursor-pointer transition-colors px-3 py-1",
                            selectedSectors.includes(sector.sector_id) 
                              ? isBarbiePink ? "bg-pink-500 hover:bg-pink-600" : ""
                              : "hover:bg-muted"
                          )}
                          onClick={() => toggleSectorSelection(sector.sector_id)}
                          data-testid={`sector-chip-${sector.sector_id}`}
                        >
                          {sector.display_name}
                          {selectedSectors.includes(sector.sector_id) && (
                            <Check className="w-3 h-3 ml-1" />
                          )}
                        </Badge>
                      ))
                    )}
                  </div>
                  {selectedSectors.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Showing organizations from: {selectedSectors.map(id => getSectorName(id)).join(', ')}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Alphabetical Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Filter by Name (A-Z)</Label>
                  <div className="flex flex-wrap gap-2">
                    {alphabetRanges.map(range => (
                      <Badge
                        key={range.value}
                        variant={alphabetFilter === range.value ? "default" : "outline"}
                        className={cn(
                          "cursor-pointer transition-colors px-3 py-1",
                          alphabetFilter === range.value 
                            ? isBarbiePink ? "bg-pink-500 hover:bg-pink-600" : ""
                            : "hover:bg-muted"
                        )}
                        onClick={() => setAlphabetFilter(range.value)}
                        data-testid={`alphabet-filter-${range.value}`}
                      >
                        {range.label}
                        {alphabetFilter === range.value && range.value !== 'all' && (
                          <Check className="w-3 h-3 ml-1" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Search and Sort */}
                <div className="flex gap-3 flex-wrap items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs mb-1 block">Search Organizations</Label>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search by name..."
                        value={bulkSearchTerm}
                        onChange={(e) => setBulkSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="bulk-search-input"
                      />
                    </div>
                  </div>
                  <div className="w-32">
                    <Label className="text-xs mb-1 block">Year</Label>
                    <Select value={bulkYearFilter} onValueChange={setBulkYearFilter}>
                      <SelectTrigger data-testid="bulk-year-filter">
                        <SelectValue placeholder="All years" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All years</SelectItem>
                        {(() => {
                          const cy = new Date().getFullYear();
                          return [cy + 1, cy, cy - 1, cy - 2, cy - 3];
                        })().map((y) => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label className="text-xs mb-1 block">Marketing Status</Label>
                    <Select value={bulkMarketingStatusFilter} onValueChange={setBulkMarketingStatusFilter}>
                      <SelectTrigger data-testid="bulk-marketing-status-filter">
                        <SelectValue placeholder="Any status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any status</SelectItem>
                        {['Booked with GS 2026', 'Booked with GS 2027', 'DO NOT MARKET', 'Potential', 'Not Interested in 2026', 'Not interested in 2027'].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-36">
                    <Label className="text-xs mb-1 block">Sort By</Label>
                    <Select value={bulkSortBy} onValueChange={setBulkSortBy}>
                      <SelectTrigger data-testid="bulk-sort-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Organization</SelectItem>
                        <SelectItem value="sector">Sector</SelectItem>
                        <SelectItem value="contact">Contact Name</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setBulkSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    title={bulkSortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  >
                    {bulkSortOrder === 'asc' ? '↑' : '↓'}
                  </Button>
                  <div className="flex gap-2 ml-auto">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={selectAllRecipients} 
                      disabled={sortedBulkOrganizations.length === 0}
                      data-testid="select-all-filtered-btn"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Select All Filtered ({sortedBulkOrganizations.length})
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearAllRecipients} 
                      disabled={selectedRecipients.length === 0}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                {/* Data-Preview prefill banner removed Feb 26 2026 — the
                    flow is handled inline on the Data Preview page now
                    via BulkEmailCampaignDialog. Operators arriving here
                    via Email Hub directly always see the full org list. */}

                {/* Organizations Table */}
                <div className="border rounded-lg max-h-[350px] overflow-y-auto">
                  {bulkLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Loader2 className="w-8 h-8 animate-spin mb-2" />
                      <p>Loading organizations...</p>
                    </div>
                  ) : sortedBulkOrganizations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Building2 className="w-12 h-12 mb-3 opacity-50" />
                      <p className="font-medium">No organizations found</p>
                      <p className="text-sm">
                        {selectedSectors.length > 0 
                          ? 'Try selecting different sectors or clearing filters'
                          : bulkSearchTerm 
                            ? 'Try a different search term'
                            : 'Select sectors above to filter organizations'}
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => { setBulkSortBy('name'); setBulkSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); }}
                          >
                            Organization {bulkSortBy === 'name' && (bulkSortOrder === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => { setBulkSortBy('sector'); setBulkSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); }}
                          >
                            Sector {bulkSortBy === 'sector' && (bulkSortOrder === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => { setBulkSortBy('contact'); setBulkSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); }}
                          >
                            Contact {bulkSortBy === 'contact' && (bulkSortOrder === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => { setBulkSortBy('email'); setBulkSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); }}
                          >
                            Email {bulkSortBy === 'email' && (bulkSortOrder === 'asc' ? '↑' : '↓')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedBulkOrganizations.map(org => (
                          <TableRow 
                            key={org.org_id} 
                            className={cn(
                              "cursor-pointer hover:bg-muted/50",
                              selectedRecipients.some(r => r.org_id === org.org_id) && "bg-primary/5"
                            )} 
                            onClick={() => toggleRecipient(org)}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedRecipients.some(r => r.org_id === org.org_id)}
                                onCheckedChange={() => toggleRecipient(org)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{org.org_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {getSectorName(org.sector_id, org.sector_name)}
                              </Badge>
                            </TableCell>
                            <TableCell>{org.contact_name}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{org.contact_email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>

                {/* Show hint when displaying limited results */}
                {!bulkSearchTerm && alphabetFilter === 'all' && selectedSectors.length === 0 && sortedBulkOrganizations.length === 20 && (
                  <p className="text-xs text-muted-foreground text-center">
                    Showing first 20 organizations. Use filters or search to see more.
                  </p>
                )}

                {selectedRecipients.length > 0 && (
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <p className="text-sm font-medium">
                      <Check className="w-4 h-4 inline mr-1" />
                      {selectedRecipients.length} recipient{selectedRecipients.length > 1 ? 's' : ''} selected
                      {selectedRecipients.length > 50 && (
                        <span className="text-xs ml-2 text-muted-foreground">
                          (Will be split into {Math.ceil(selectedRecipients.length / 50)} batches of 50)
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Campaign Settings Card - Below Recipients */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Campaign Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left - Campaign Details */}
                  <div className="space-y-4">
                    <div>
                      <Label>Campaign Name *</Label>
                      <Input
                        value={bulkCampaignName}
                        onChange={(e) => setBulkCampaignName(e.target.value)}
                        placeholder="e.g., Summer Photo Promotion"
                        data-testid="bulk-campaign-name"
                      />
                    </div>

                    <div>
                      <Label>Use Template (Optional)</Label>
                      <Select value={bulkSelectedTemplate || 'none'} onValueChange={(v) => handleBulkTemplateSelect(v === 'none' ? '' : v)}>
                        <SelectTrigger data-testid="bulk-template-select">
                          <SelectValue placeholder="Select a template..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None - Write custom</SelectItem>
                          {templates.map(t => (
                            <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Campaign Templates Section */}
                    <div className="p-3 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Campaign Templates</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCampaignTemplateName('');
                            setEditingCampaignTemplate(null);
                            setShowSaveCampaignDialog(true);
                          }}
                          disabled={!bulkSubject && !bulkBody}
                          data-testid="save-campaign-template-btn"
                        >
                          <Save className="w-3 h-3 mr-1" />
                          Save Current
                        </Button>
                      </div>
                      
                      {campaignTemplatesLoading ? (
                        <div className="text-center py-2">
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        </div>
                      ) : campaignTemplates.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-2">
                          No saved campaign templates yet
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {campaignTemplates.map(ct => (
                            <div 
                              key={ct.template_id}
                              className={cn(
                                "flex items-center justify-between p-2 rounded border cursor-pointer hover:bg-muted/50 transition-colors",
                                selectedCampaignTemplate === ct.template_id && "border-primary bg-primary/5"
                              )}
                            >
                              <div 
                                className="flex-1 min-w-0"
                                onClick={() => loadCampaignTemplate(ct.template_id)}
                              >
                                <p className="text-sm font-medium truncate">{ct.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {ct.subject_template || 'No subject'}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 ml-2">
                                {selectedCampaignTemplate === ct.template_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateCampaignTemplate(ct.template_id)}
                                    disabled={savingCampaignTemplate}
                                    title="Update this template with current settings"
                                    data-testid={`update-campaign-${ct.template_id}`}
                                  >
                                    {savingCampaignTemplate ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Save className="w-3 h-3" />
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteCampaignTemplate(ct.template_id)}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete template"
                                  data-testid={`delete-campaign-${ct.template_id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Subject *</Label>
                      <Input
                        value={bulkSubject}
                        onChange={(e) => setBulkSubject(e.target.value)}
                        placeholder="Email subject with {{org_name}}"
                        data-testid="bulk-subject"
                      />
                    </div>

                    <div>
                      <Label className="flex items-center justify-between">
                        <span>Body *</span>
                        <div className="flex items-center gap-2">
                          <Select value={bulkPolishTone} onValueChange={setBulkPolishTone}>
                            <SelectTrigger className="w-32 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="professional">Professional</SelectItem>
                              <SelectItem value="friendly">Friendly</SelectItem>
                              <SelectItem value="formal">Formal</SelectItem>
                              <SelectItem value="casual">Casual</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleBulkPolishEmail}
                            disabled={bulkPolishing || !bulkBody}
                            className="h-7"
                          >
                            {bulkPolishing ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="w-3 h-3 mr-1" />
                            )}
                            AI Assist
                          </Button>
                        </div>
                      </Label>
                      <Textarea
                        value={bulkBody}
                        onChange={(e) => setBulkBody(e.target.value)}
                        placeholder="Dear {{contact_name}},&#10;&#10;Write your email content here..."
                        rows={8}
                        data-testid="bulk-body"
                        className="mt-2"
                      />
                    </div>

                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-xs font-medium mb-2">Available Merge Variables:</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="cursor-pointer" onClick={() => setBulkBody(prev => prev + '{{org_name}}')}>{"{{org_name}}"}</Badge>
                        <Badge variant="outline" className="cursor-pointer" onClick={() => setBulkBody(prev => prev + '{{contact_name}}')}>{"{{contact_name}}"}</Badge>
                        <Badge variant="outline" className="cursor-pointer" onClick={() => setBulkBody(prev => prev + '{{email}}')}>{"{{email}}"}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Click to insert at cursor position</p>
                    </div>

                    {/* Attachments for Bulk Campaign */}
                    <div>
                      <Label className="flex items-center gap-2">
                        Attachments
                        {bulkAttachments.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {bulkAttachments.length}
                          </Badge>
                        )}
                      </Label>
                      <div className="mt-2 space-y-2">
                        <AttachmentList
                          attachments={bulkAttachments}
                          onRemove={removeBulkAttachment}
                          showPreview={true}
                          emptyMessage="No attachments"
                        />
                        <input
                          ref={bulkFileInputRef}
                          type="file"
                          multiple
                          onChange={handleBulkFileUpload}
                          className="hidden"
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => bulkFileInputRef.current?.click()}
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          Add Attachments
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Right - Schedule & Submit */}
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <Label className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4" />
                        Schedule Send *
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Scheduled time must be at least 2 hours from now. Emails will be sent with 10-second intervals.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Date</Label>
                          <Input
                            type="date"
                            value={bulkScheduledDate}
                            onChange={(e) => setBulkScheduledDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            data-testid="bulk-schedule-date"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Time</Label>
                          <Input
                            type="time"
                            value={bulkScheduledTime}
                            onChange={(e) => setBulkScheduledTime(e.target.value)}
                            data-testid="bulk-schedule-time"
                          />
                        </div>
                      </div>
                    </div>

                    {selectedRecipients.length > 0 && bulkSubject && bulkBody && (
                      <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                        <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">Ready to Schedule</h4>
                        <ul className="text-sm text-green-700 dark:text-green-400 space-y-1">
                          <li>• {selectedRecipients.length} recipient{selectedRecipients.length > 1 ? 's' : ''}</li>
                          <li>• Subject: {bulkSubject.substring(0, 40)}{bulkSubject.length > 40 ? '...' : ''}</li>
                          {bulkScheduledDate && bulkScheduledTime && (
                            <li>• Scheduled: {bulkScheduledDate} at {bulkScheduledTime}</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <Button
                      className="w-full"
                      size="lg"
                      onClick={createBulkEmailCampaign}
                      disabled={bulkCreating || selectedRecipients.length === 0 || !bulkSubject || !bulkBody || !bulkScheduledDate || !bulkScheduledTime}
                      data-testid="create-bulk-campaign-btn"
                    >
                      {bulkCreating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Calendar className="w-4 h-4 mr-2" />
                      )}
                      {selectedRecipients.length > 50 
                        ? `Schedule ${selectedRecipients.length} Emails (${Math.ceil(selectedRecipients.length / 50)} Batches)`
                        : `Schedule ${selectedRecipients.length} Email${selectedRecipients.length !== 1 ? 's' : ''}`
                      }
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Polish Confirmation Dialog for Bulk Email */}
          <Dialog open={showBulkPolishConfirm} onOpenChange={setShowBulkPolishConfirm}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  AI Polished Text
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Original:</Label>
                  <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {bulkOriginalBody}
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">AI Polished ({bulkPolishTone}):</Label>
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {bulkPolishedText}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={rejectBulkPolishedText}>
                    Keep Original
                  </Button>
                  <Button onClick={acceptBulkPolishedText}>
                    <Check className="w-4 h-4 mr-1" />
                    Use Polished Text
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Scheduled Campaigns */}
          <Card className={cn("mt-6", isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Scheduled Campaigns
                </span>
                <Button variant="outline" size="sm" onClick={fetchScheduledEmails} disabled={scheduledLoading}>
                  {scheduledLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scheduledLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : scheduledCampaigns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No scheduled campaigns</p>
                  <p className="text-sm">Create a campaign above to schedule bulk emails</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scheduledCampaigns.map(campaign => (
                    <div key={campaign._id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium">{campaign.campaign_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            by {campaign.created_by_name} • Scheduled: {formatDate(campaign.scheduled_at)} {formatTime(campaign.scheduled_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline">{campaign.total} emails</Badge>
                          {campaign.pending > 0 && <Badge>{campaign.pending} pending</Badge>}
                          {campaign.sent > 0 && <Badge variant="secondary" className="bg-green-100 text-green-700">{campaign.sent} sent</Badge>}
                          {campaign.failed > 0 && <Badge variant="destructive">{campaign.failed} failed</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setViewingBulkEmail(campaign)}>
                          <Eye className="w-3 h-3 mr-1" />
                          View Details
                        </Button>
                        {campaign.pending > 0 && (
                          <Button variant="destructive" size="sm" onClick={() => deleteBulkCampaign(campaign._id)}>
                            <Trash2 className="w-3 h-3 mr-1" />
                            Cancel Pending
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaign Results Tab — admins audit every bulk campaign + resend
            failed recipients (esp. those killed by the Mongo timeout
            "customer-apps-shard-00-01.fi6eho.mongodb.net:27017: The read
            operation timed out." that operators flagged in Mar 2026). */}
        <TabsContent value="campaigns" className="mt-6">
          <CampaignResultsTab />
        </TabsContent>

        {/* Email Audit Tab */}
        <TabsContent value="audit" className="mt-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Audit Log
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                View all sent emails and their delivery status
              </p>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-3 mb-4">
                <Input
                  placeholder="Search by email or subject..."
                  value={auditSearchTerm}
                  onChange={(e) => setAuditSearchTerm(e.target.value)}
                  className="w-64"
                />
                <Select value={auditOrgFilter} onValueChange={setAuditOrgFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Organizations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map(o => (
                      <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Emails Table */}
              {auditLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : auditEmails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No emails found</p>
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>To</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Sent By</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Nudge</TableHead>
                          <TableHead className="w-20">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditEmails.map(email => (
                          <TableRow key={email.email_id}>
                            <TableCell className="whitespace-nowrap">
                              {formatDate(email.sent_at)}
                              <br />
                              <span className="text-xs text-muted-foreground">{formatTime(email.sent_at)}</span>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium">{email.to_name || '-'}</p>
                              <p className="text-sm text-muted-foreground">{email.to_email}</p>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">{email.subject}</TableCell>
                            <TableCell>{email.user_name || email.sent_by || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={email.status === 'sent' ? 'default' : 'secondary'}>
                                {email.sent_via_gmail ? 'Sent via Gmail' : email.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={email.nudge_enabled ? 'default' : 'outline'}
                                className={cn(
                                  email.nudge_enabled && 'bg-green-600 hover:bg-green-700',
                                  !email.nudge_enabled && 'text-muted-foreground'
                                )}
                              >
                                {email.nudge_enabled 
                                  ? `${email.nudge_count || 0}/3` 
                                  : 'Off'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => setViewingAuditEmail(email)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {auditTotalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => fetchAuditEmails(auditPage - 1)} disabled={auditPage <= 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground">Page {auditPage} of {auditTotalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => fetchAuditEmails(auditPage + 1)} disabled={auditPage >= auditTotalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Detail Dialog for Audit */}
      <Dialog open={!!viewingAuditEmail} onOpenChange={() => setViewingAuditEmail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Details</DialogTitle>
          </DialogHeader>
          {viewingAuditEmail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">To:</p>
                  <p className="font-medium">{viewingAuditEmail.to_name}</p>
                  <p>{viewingAuditEmail.to_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sent:</p>
                  <p>{formatDate(viewingAuditEmail.sent_at)} {formatTime(viewingAuditEmail.sent_at)}</p>
                  <p className="text-muted-foreground">By: {viewingAuditEmail.user_name || viewingAuditEmail.sent_by}</p>
                </div>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Subject:</p>
                <p className="font-medium">{viewingAuditEmail.subject}</p>
              </div>
              {(viewingAuditEmail.cc || viewingAuditEmail.bcc) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="audit-cc-bcc">
                  {viewingAuditEmail.cc && (
                    <div>
                      <p className="text-muted-foreground">Cc:</p>
                      <p className="break-all">{viewingAuditEmail.cc}</p>
                    </div>
                  )}
                  {viewingAuditEmail.bcc && (
                    <div>
                      <p className="text-muted-foreground">Bcc:</p>
                      <p className="break-all">{viewingAuditEmail.bcc}</p>
                    </div>
                  )}
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-sm">Body:</p>
                <div className="p-4 bg-muted rounded-lg whitespace-pre-wrap text-sm max-h-[300px] overflow-y-auto">
                  {viewingAuditEmail.body}
                </div>
              </div>
              
              {/* Nudge Settings */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Follow-up Nudges</p>
                    <p className="text-sm text-muted-foreground">
                      {viewingAuditEmail.nudge_enabled 
                        ? `Enabled - ${viewingAuditEmail.nudge_count || 0}/3 sent` 
                        : 'Disabled - No automatic follow-ups'}
                    </p>
                    {viewingAuditEmail.next_nudge_at && viewingAuditEmail.nudge_enabled && (
                      <p className="text-xs text-muted-foreground">
                        Next nudge: {formatDate(viewingAuditEmail.next_nudge_at)} {formatTime(viewingAuditEmail.next_nudge_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="audit-nudge-toggle" className="text-sm">
                      {viewingAuditEmail.nudge_enabled ? 'On' : 'Off'}
                    </Label>
                    <input
                      type="checkbox"
                      id="audit-nudge-toggle"
                      checked={viewingAuditEmail.nudge_enabled || false}
                      onChange={(e) => toggleEmailNudge(viewingAuditEmail, e.target.checked)}
                      disabled={updatingNudge}
                      className="w-5 h-5 rounded cursor-pointer"
                      data-testid="audit-nudge-toggle"
                    />
                    {updatingNudge && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <AlertDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Email Preview - Confirm Before Sending</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">To:</span>
                    <p className="font-medium">{previewData?.to}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">From:</span>
                    <p className="font-medium">{previewData?.from}</p>
                  </div>
                </div>
                {(previewData?.cc?.length > 0 || previewData?.bcc?.length > 0) && (
                  <div className="grid grid-cols-2 gap-4 text-sm" data-testid="preview-cc-bcc">
                    {previewData?.cc?.length > 0 && (
                      <div>
                        <span className="text-muted-foreground">Cc:</span>
                        <p className="font-medium break-all">{previewData.cc.join(', ')}</p>
                      </div>
                    )}
                    {previewData?.bcc?.length > 0 && (
                      <div>
                        <span className="text-muted-foreground">Bcc:</span>
                        <p className="font-medium break-all">{previewData.bcc.join(', ')}</p>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground text-sm">Subject:</span>
                  <p className="font-medium text-lg">{previewData?.subject}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Body:</span>
                  <div className="mt-2 p-4 bg-muted rounded-lg whitespace-pre-wrap text-sm max-h-60 overflow-y-auto">
                    {previewData?.body}
                  </div>
                </div>
                {composeData.attachments?.length > 0 && (
                  <div>
                    <span className="text-muted-foreground text-sm flex items-center gap-1 mb-2">
                      <Paperclip className="w-4 h-4" />
                      Attachments ({composeData.attachments.length})
                    </span>
                    <AttachmentList
                      attachments={composeData.attachments}
                      showPreview={true}
                    />
                  </div>
                )}
                {composeData.ai_polished && (
                  <Badge variant="secondary" className="gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Polished
                  </Badge>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Edit</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendEmail} disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Confirm & Send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Polish Confirmation Dialog */}
      <Dialog open={showPolishConfirm} onOpenChange={setShowPolishConfirm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI Polished Result - Review Before Applying
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-muted-foreground">Original</Label>
              <div className="mt-2 p-4 bg-muted rounded-lg whitespace-pre-wrap text-sm max-h-80 overflow-y-auto">
                {originalBody}
              </div>
            </div>
            <div>
              <Label className="text-primary">AI Polished</Label>
              <div className="mt-2 p-4 bg-primary/5 border border-primary/20 rounded-lg whitespace-pre-wrap text-sm max-h-80 overflow-y-auto">
                {polishedText}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={rejectPolishedText}>
              Keep Original
            </Button>
            <Button onClick={acceptPolishedText}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Apply Polished Text
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select 
                  value={templateForm.category || 'none'} 
                  onValueChange={(v) => setTemplateForm({...templateForm, category: v === 'none' ? '' : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject *</Label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Body *</Label>
                <DebouncedTextarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
                  rows={8}
                  required
                  debounceMs={300}
                />
              </div>
              
              {/* Template Attachments */}
              <div>
                <Label className="flex items-center gap-2">
                  Template Attachments
                  {templateForm.attachments.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {templateForm.attachments.length}
                    </Badge>
                  )}
                </Label>
                <div className="mt-2 space-y-2">
                  <AttachmentList
                    attachments={templateForm.attachments}
                    onRemove={(id) => removeAttachment(id, true)}
                    showPreview={true}
                    emptyMessage="No attachments added"
                  />
                  <input
                    ref={templateFileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleFileUpload(e, true)}
                    className="hidden"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm" 
                    onClick={() => templateFileInputRef.current?.click()}
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Add Attachments
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
              <Button type="submit">{editingTemplate ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Save Campaign Template Dialog */}
      <Dialog open={showSaveCampaignDialog} onOpenChange={setShowSaveCampaignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Campaign Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={campaignTemplateName}
                onChange={(e) => setCampaignTemplateName(e.target.value)}
                placeholder="e.g., Summer Photo Promo Template"
                data-testid="campaign-template-name-input"
              />
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs font-medium mb-2">This will save:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Subject: {bulkSubject || '(empty)'}</li>
                <li>• Body: {bulkBody ? `${bulkBody.substring(0, 50)}...` : '(empty)'}</li>
                <li>• Attachments: {bulkAttachments.length} file(s)</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowSaveCampaignDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={saveCampaignTemplate}
              disabled={savingCampaignTemplate || !campaignTemplateName.trim()}
              data-testid="confirm-save-campaign-template"
            >
              {savingCampaignTemplate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
